import { Box, MenuItem, Select, Skeleton, Typography } from '@mui/material';
import { useBuilderVocabulary } from '../../hooks/useBuilderVocabulary';
import { BuilderVocabularyItem } from '../../types/SimilarlySituated/BuilderVocabularyItem.type';
import { BuilderVocabulary } from '../../types/SimilarlySituated/BuilderVocabulary.type';
import PillGroup from './PillGroup';

const labelStyle = {
  fontSize: '11px',
  fontWeight: 600,
  letterSpacing: '0.08em',
  color: '#718096',
  marginBottom: '6px',
};

interface BaseVocabularyFieldProps {
  label: string;
  vocabulary: BuilderVocabulary;
  placeholder?: string;
  disabled?: boolean;
}

interface SingleVocabularyFieldProps extends BaseVocabularyFieldProps {
  multiple?: false;
  value?: BuilderVocabularyItem;
  onChange: (option: BuilderVocabularyItem | undefined) => void;
}

// multiple selection is rendered as pills only
interface MultipleVocabularyFieldProps extends Omit<BaseVocabularyFieldProps, 'placeholder'> {
  multiple: true;
  value: BuilderVocabularyItem[];
  onChange: (options: BuilderVocabularyItem[]) => void;
  placeholder?: never;
}

type VocabularyFieldProps = SingleVocabularyFieldProps | MultipleVocabularyFieldProps;

function VocabularyField(props: VocabularyFieldProps) {
  const { label, vocabulary, placeholder, disabled } = props;
  const { data: options, isPending, isError } = useBuilderVocabulary(vocabulary);

  if (isError) return null;

  const renderPills = (items: BuilderVocabularyItem[]) => {
    if (props.multiple) {
      const { value, onChange } = props;
      const toggle = (option: BuilderVocabularyItem) =>
        onChange(
          value.some((item) => item.id === option.id)
            ? value.filter((item) => item.id !== option.id)
            : [...value, option],
        );

      return (
        <PillGroup options={items} selectedIds={value.map((item) => item.id)} onChange={toggle} disabled={disabled} />
      );
    }

    return (
      <PillGroup
        options={items}
        selectedIds={props.value ? [props.value.id] : []}
        onChange={props.onChange}
        disabled={disabled}
      />
    );
  };

  const renderControl = () => {
    if (isPending) {
      return <Skeleton variant='rounded' height={placeholder ? 40 : 34} width={placeholder ? undefined : 260} />;
    }

    if (props.multiple || !placeholder) return renderPills(options);

    const { value, onChange } = props;

    return (
      <Select
        size='small'
        fullWidth
        displayEmpty
        disabled={disabled}
        value={value ? String(value.id) : ''}
        onChange={(event) => onChange(options.find((option) => String(option.id) === event.target.value))}
        renderValue={() => (value ? value.name : <span style={{ color: '#A0AEC0' }}>{placeholder}</span>)}
        sx={{
          backgroundColor: '#fff',
          fontSize: '13px',
          '.MuiSelect-select': { cursor: disabled ? 'not-allowed !important' : 'pointer' },
        }}
      >
        <MenuItem value='' sx={{ fontSize: '13px' }}>
          {placeholder}
        </MenuItem>
        {options.map((option) => (
          <MenuItem key={option.id} value={String(option.id)} sx={{ fontSize: '13px' }}>
            {option.name}
          </MenuItem>
        ))}
      </Select>
    );
  };

  return (
    <Box>
      <Typography sx={labelStyle}>{label}</Typography>
      {renderControl()}
    </Box>
  );
}

export default VocabularyField;
